<template>
  <div>
    <div class="flex justify-between items-center mb-6">
      <h2 class="font-header text-[3rem] font-bold text-primary">Sales & Receipts</h2>
    </div>

    <div class="mb-6 border-b border-gray-200">
      <div class="flex gap-4">
        <button @click="activeTab = 'sales'" :class="[
          'px-6 py-3 font-header text-[1.5rem] font-bold border-b-2 transition-colors',
          activeTab === 'sales'
            ? 'border-green-700 text-green-700'
            : 'border-transparent text-gray-600 hover:text-gray-900'
        ]">
          Sales
        </button>
        <button @click="activeTab = 'discounts'" :class="[
          'px-6 py-3 font-header text-[1.5rem] font-bold border-b-2 transition-colors',
          activeTab === 'discounts'
            ? 'border-green-700 text-green-700'
            : 'border-transparent text-gray-600 hover:text-gray-900'
        ]">
          Discount Codes
        </button>
        <button @click="activeTab = 'donations'" :class="[
          'px-6 py-3 font-header text-[1.5rem] font-bold border-b-2 transition-colors',
          activeTab === 'donations'
            ? 'border-green-700 text-green-700'
            : 'border-transparent text-gray-600 hover:text-gray-900'
        ]">
          Donations
        </button>
      </div>
    </div>

    <div v-if="activeTab === 'sales'" class="bg-white rounded-lg shadow-md overflow-hidden">
      <div v-if="loading" class="p-8 text-center">
        <Loader size="large" />
      </div>
      <div v-else class="w-full max-w-full overflow-x-auto">
        <table class="w-full min-w-max">
          <thead class="bg-green-700 text-white">
            <tr>
              <th class="px-4 py-3 text-left font-header text-[1.125rem] font-bold whitespace-nowrap">Payment Ref</th>
              <th class="px-4 py-3 text-left font-header text-[1.125rem] font-bold whitespace-nowrap">Customer</th>
              <th class="px-4 py-3 text-left font-header text-[1.125rem] font-bold whitespace-nowrap">Item</th>
              <th class="px-4 py-3 text-left font-header text-[1.125rem] font-bold whitespace-nowrap">Format</th>
              <th class="px-4 py-3 text-left font-header text-[1.125rem] font-bold whitespace-nowrap">Discount</th>
              <th class="px-4 py-3 text-left font-header text-[1.125rem] font-bold whitespace-nowrap">Total</th>
              <th class="px-4 py-3 text-left font-header text-[1.125rem] font-bold whitespace-nowrap">Purchase Date</th>
              <th class="px-4 py-3 text-left font-header text-[1.125rem] font-bold whitespace-nowrap">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="order in groupedSales" :key="order.transaction_id"
              class="border-b border-creme-200 hover:bg-creme-50">
              <td class="px-4 py-3 font-body text-[1.125rem] leading-[1.75]">
                <span class="font-mono text-sm bg-creme-200 px-2 py-1 rounded text-gray-900">{{ order.transaction_id
                }}</span>
              </td>
              <td class="px-4 py-3 font-body text-[1.125rem] text-gray-900 leading-[1.75]">{{ order.customer_name ||
                'Guest Customer' }}</td>
              <td class="px-4 py-3 font-body text-[1.125rem] leading-[1.75]">
                <div class="space-y-1">
                  <div v-for="(item, idx) in order.items" :key="idx">
                    <div class="font-body text-[1.125rem] text-gray-900 leading-[1.75]">
                      {{ item.book_title }}{{ item.quantity > 1 ? ` x${item.quantity}` : '' }}
                    </div>
                    <div class="font-body text-[1.125rem] text-gray-600 leading-[1.75]" data-no-translate>
                      <template v-if="item.format === 'Ticket' && item.book_author">For {{ item.book_author
                      }}</template>
                      <template v-else-if="item.book_author">By {{ item.book_author }}</template>
                    </div>
                    <div class="font-body text-[1.125rem] text-gray-600 leading-[1.75]">{{ item.format }}</div>
                  </div>
                  <div v-if="order.items.length > 1" class="font-body text-xs text-gray-500 mt-1 leading-[1.75]">
                    {{ order.items.length }} items
                  </div>
                </div>
              </td>
              <td class="px-4 py-3 font-body text-[1.125rem] text-gray-900 leading-[1.75]">
                <span v-for="(item, idx) in order.items" :key="idx" class="capitalize">
                  {{ item.format }}<span v-if="idx < order.items.length - 1">, </span>
                </span>
              </td>
              <td class="px-4 py-3 font-body text-[1.125rem] leading-[1.75]">
                <div v-if="order.discount_code" class="space-y-1">
                  <div class="font-body text-[1.125rem] text-green-700 leading-[1.75]" data-no-translate>{{
                    order.discount_code }}</div>
                  <div v-if="order.total_discount > 0" class="font-body text-xs text-green-600 leading-[1.75]">
                    -${{ order.total_discount.toFixed(2) }}
                  </div>
                </div>
                <div v-else class="font-body text-[1.125rem] text-gray-400 leading-[1.75]">—</div>
              </td>
              <td class="px-4 py-3 font-body text-[1.125rem] text-gray-900 leading-[1.75]">
                <div>
                  <div class="font-body text-[1.125rem] text-gray-900 leading-[1.75]">${{ order.total.toFixed(2) }}
                  </div>
                  <div v-if="order.subtotal !== order.total" class="font-body text-xs text-gray-500 leading-[1.75]">
                    Subtotal: ${{ order.subtotal.toFixed(2) }}
                  </div>
                </div>
              </td>
              <td class="px-4 py-3 font-body text-[1.125rem] text-gray-900 leading-[1.75]">{{
                formatDate(order.purchase_date) }}</td>
              <td class="px-4 py-3">
                <button @click="viewReceipt(order)"
                  class="text-green-700 hover:text-green-800 font-body text-[1.125rem] leading-[1.75] whitespace-nowrap">View
                  Receipt</button>
              </td>
            </tr>
            <tr v-if="!loading && groupedSales.length === 0">
              <td colspan="8" class="px-4 py-8 text-center text-gray-500 font-marketing">No sales recorded yet</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-if="pagination && pagination.total > itemsPerPage"
        class="px-4 py-4 border-t border-creme-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div class="text-sm text-gray-600 font-marketing">
          Showing {{ ((currentPage - 1) * itemsPerPage) + 1 }} to {{ Math.min(currentPage * itemsPerPage,
            pagination.total) }} of {{ pagination.total }} sales
        </div>
        <div class="flex items-center gap-2 max-w-full overflow-x-auto">
          <button @click="goToPage(currentPage - 1)" :disabled="currentPage === 1"
            class="px-4 py-2 rounded-md font-marketing transition-colors"
            :class="currentPage === 1 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-green-700 text-white hover:bg-green-800'">
            Previous
          </button>
          <div class="flex items-center gap-1 min-w-max">
            <button v-for="page in visiblePages" :key="page" @click="typeof page === 'number' && goToPage(page)"
              :disabled="typeof page === 'string'" class="px-3 py-2 rounded-md font-marketing transition-colors" :class="typeof page === 'string'
                ? 'cursor-default'
                : currentPage === page
                  ? 'bg-green-700 text-white'
                  : 'bg-creme-100 text-gray-700 hover:bg-creme-200'">
              {{ typeof page === 'string' ? '…' : page }}
            </button>
          </div>
          <button @click="goToPage(currentPage + 1)" :disabled="!pagination.next_page"
            class="px-4 py-2 rounded-md font-marketing transition-colors"
            :class="!pagination.next_page ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-green-700 text-white hover:bg-green-800'">
            Next
          </button>
        </div>
      </div>
    </div>

    <div v-if="activeTab === 'donations'" class="bg-white rounded-lg shadow-md overflow-hidden">
      <div v-if="donationsLoading" class="p-8 text-center">
        <Loader size="large" />
      </div>
      <div v-else class="w-full max-w-full overflow-x-auto">
        <table class="w-full min-w-max">
          <thead class="bg-green-700 text-white">
            <tr>
              <th class="px-4 py-3 text-left font-header text-[1.125rem] font-bold whitespace-nowrap">Donor</th>
              <th class="px-4 py-3 text-left font-header text-[1.125rem] font-bold whitespace-nowrap">Email</th>
              <th class="px-4 py-3 text-left font-header text-[1.125rem] font-bold whitespace-nowrap">Amount</th>
              <th class="px-4 py-3 text-left font-header text-[1.125rem] font-bold whitespace-nowrap">Status</th>
              <th class="px-4 py-3 text-left font-header text-[1.125rem] font-bold whitespace-nowrap">Note</th>
              <th class="px-4 py-3 text-left font-header text-[1.125rem] font-bold whitespace-nowrap">Date</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="donation in donations" :key="donation.id"
              class="border-b border-creme-200 hover:bg-creme-50">
              <td class="px-4 py-3 font-body text-[1.125rem] text-gray-900 leading-[1.75]">{{ donation.donor_name ||
                'Anonymous' }}</td>
              <td class="px-4 py-3 font-body text-[1.125rem] text-gray-900 leading-[1.75]" data-no-translate>{{
                donation.donor_email || '—' }}</td>
              <td class="px-4 py-3 font-body text-[1.125rem] text-gray-900 leading-[1.75]">${{
                parseFloat(donation.amount).toFixed(2) }}</td>
              <td class="px-4 py-3 font-body text-[1.125rem] leading-[1.75]">
                <span class="px-2 py-1 rounded text-sm capitalize" :class="donation.payment_status === 'paid'
                  ? 'bg-green-100 text-green-800'
                  : donation.payment_status === 'pending'
                    ? 'bg-yellow-100 text-yellow-800'
                    : 'bg-gray-100 text-gray-700'">
                  {{ donation.payment_status || 'unknown' }}
                </span>
              </td>
              <td class="px-4 py-3 font-body text-[1.125rem] text-gray-600 leading-[1.75] max-w-xs truncate">{{
                donation.note || '—' }}</td>
              <td class="px-4 py-3 font-body text-[1.125rem] text-gray-900 leading-[1.75]">{{
                formatDate(donation.created_at) }}</td>
            </tr>
            <tr v-if="!donationsLoading && donations.length === 0">
              <td colspan="6" class="px-4 py-8 text-center text-gray-500 font-marketing">No donations recorded yet</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <BaseModal v-model="showReceiptModal" content-class="max-w-2xl max-h-[90vh]" :show-close-button="false">
      <div class="border-b border-creme-300 flex justify-between items-center mb-6 pb-4">
        <h3 class="text-2xl font-marketing font-bold text-primary">Receipt</h3>
        <button @click="closeReceiptModal" class="text-gray-500 hover:text-gray-700 text-2xl">&times;</button>
      </div>

      <div v-html="receiptModalContent" class="receipt-content"></div>

      <div class="border-t border-creme-300 flex justify-end pt-6 mt-6">
        <button @click="downloadReceipt"
          class="px-6 py-2 bg-green-700 text-white rounded-md hover:bg-green-800 transition-colors font-marketing">
          Download PDF
        </button>
      </div>
    </BaseModal>


    <div v-if="activeTab === 'discounts'" data-no-translate>
      <div class="flex justify-between items-center mb-6">
        <h2 class="text-3xl font-marketing font-bold text-primary">Discount Codes</h2>
        <button @click="showDiscountForm = true; editingDiscountCode = null"
          class="px-6 py-2 bg-green-700 text-white rounded-md hover:bg-green-800 transition-colors font-marketing">
          Add New Code
        </button>
      </div>

      <div v-if="showDiscountForm" class="bg-white p-6 rounded-lg shadow-md mb-6">
        <h3 class="text-xl font-marketing font-semibold mb-4">{{ editingDiscountCode ? 'Edit' : 'Add' }} Discount Code
        </h3>
        <form @submit.prevent="saveDiscountCode" class="space-y-4">
          <div class="grid grid-cols-2 gap-4">
            <div>
              <label class="block text-sm font-marketing mb-1">Code</label>
              <input v-model="discountFormData.code" type="text" required
                class="w-full px-3 py-2 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-sans" />
            </div>
            <div>
              <label class="block text-sm font-marketing mb-1">Description (optional)</label>
              <input v-model="discountFormData.description" type="text"
                class="w-full px-3 py-2 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-sans" />
            </div>
            <div>
              <label class="block text-sm font-marketing mb-1">Discount Type</label>
              <select v-model="discountFormData.discount_type" required
                class="w-full px-3 py-2 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-sans">
                <option value="percentage">Percentage</option>
                <option value="fixed">Fixed Amount</option>
              </select>
            </div>
            <div>
              <label class="block text-sm font-marketing mb-1">{{ discountFormData.discount_type === 'percentage' ?
                'Discount %' : 'Discount Amount ($)' }}</label>
              <input v-model.number="discountFormData.discount_value" type="number" step="0.01" min="0"
                :max="discountFormData.discount_type === 'percentage' ? 100 : null" required
                class="w-full px-3 py-2 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-sans" />
            </div>
            <div>
              <label class="block text-sm font-marketing mb-1">Min Purchase Amount (optional)</label>
              <input v-model.number="discountFormData.min_purchase_amount" type="number" step="0.01" min="0"
                class="w-full px-3 py-2 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-sans" />
            </div>
            <div>
              <label class="block text-sm font-marketing mb-1">Max Discount Amount (optional)</label>
              <input v-model.number="discountFormData.max_discount_amount" type="number" step="0.01" min="0"
                class="w-full px-3 py-2 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-sans" />
            </div>
            <div>
              <label class="block text-sm font-marketing mb-1">Valid From</label>
              <input v-model="discountFormData.valid_from" type="datetime-local" required
                class="w-full px-3 py-2 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-sans" />
            </div>
            <div>
              <label class="block text-sm font-marketing mb-1">Valid Until (optional)</label>
              <input v-model="discountFormData.valid_until" type="datetime-local"
                class="w-full px-3 py-2 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-sans" />
            </div>
            <div>
              <label class="block text-sm font-marketing mb-1">Usage Limit (optional)</label>
              <input v-model.number="discountFormData.usage_limit" type="number" min="1"
                class="w-full px-3 py-2 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-sans" />
            </div>
          </div>
          <div class="flex gap-2">
            <button type="submit"
              class="px-6 py-2 bg-green-700 text-white rounded-md hover:bg-green-800 transition-colors font-marketing">
              {{ editingDiscountCode ? 'Update' : 'Create' }}
            </button>
            <button type="button" @click="cancelDiscountForm"
              class="px-6 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors font-marketing">
              Cancel
            </button>
          </div>
        </form>
      </div>

      <div v-if="discountError" class="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
        <p class="text-red-800 font-marketing">Failed to load discount codes. Please refresh the page.</p>
      </div>
      <div v-if="discountLoading" class="bg-white rounded-lg shadow-md p-8 text-center">
        <p class="text-gray-600 font-marketing">Loading discount codes...</p>
      </div>
      <div v-else class="bg-white rounded-lg shadow-md overflow-hidden">
        <div class="w-full max-w-full overflow-x-auto">
          <table class="w-full min-w-max">
            <thead class="bg-green-700 text-white">
              <tr>
                <th class="px-4 py-3 text-left font-marketing whitespace-nowrap">Code</th>
                <th class="px-4 py-3 text-left font-marketing whitespace-nowrap">Type</th>
                <th class="px-4 py-3 text-left font-marketing whitespace-nowrap">Discount</th>
                <th class="px-4 py-3 text-left font-marketing whitespace-nowrap">Valid From</th>
                <th class="px-4 py-3 text-left font-marketing whitespace-nowrap">Valid Until</th>
                <th class="px-4 py-3 text-left font-marketing whitespace-nowrap">Uses</th>
                <th class="px-4 py-3 text-left font-marketing whitespace-nowrap">Status</th>
                <th class="px-4 py-3 text-left font-marketing whitespace-nowrap">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="safeDiscountCodes.length === 0 && !discountError">
                <td colspan="8" class="px-4 py-8 text-center text-gray-500 font-marketing">
                  No discount codes found. Click "Add New Code" to create one.
                </td>
              </tr>
              <template v-for="(code, index) in safeDiscountCodes" :key="code?.id || `code-${index}`">
                <tr class="border-b border-creme-200 hover:bg-creme-50">
                  <td class="px-4 py-3 font-marketing font-semibold text-gray-900 whitespace-nowrap" data-no-translate>
                    {{ code?.code || '' }}</td>
                  <td class="px-4 py-3 font-marketing text-gray-900 capitalize whitespace-nowrap">{{ code?.discount_type
                    || '' }}</td>
                  <td class="px-4 py-3 font-marketing text-gray-900 whitespace-nowrap" data-no-translate>
                    {{ code?.discount_type === 'percentage' ? `${code?.discount_value || 0}%` :
                      `$${Number(code?.discount_value || 0).toFixed(2)}` }}
                  </td>
                  <td class="px-4 py-3 font-marketing text-gray-900 whitespace-nowrap">{{ code?.valid_from ?
                    formatDiscountDate(code.valid_from) : '' }}</td>
                  <td class="px-4 py-3 font-marketing text-gray-900 whitespace-nowrap">{{ code?.valid_until ?
                    formatDiscountDate(code.valid_until) : 'No expiry' }}</td>
                  <td class="px-4 py-3 font-marketing text-gray-900 whitespace-nowrap">
                    {{ code?.used_by_emails ? code.used_by_emails.length : 0 }}{{ code?.usage_limit ? ` /
                    ${code.usage_limit}` : '' }}
                    <button
                      v-if="code?.used_by_emails && Array.isArray(code.used_by_emails) && code.used_by_emails.length > 0"
                      @click="code.showEmails = !code.showEmails"
                      class="ml-2 text-xs text-blue-600 hover:text-blue-800 font-body whitespace-nowrap">
                      ({{ code.used_by_emails.length }} {{ code.used_by_emails.length === 1 ? 'user' : 'users' }})
                    </button>
                  </td>
                  <td class="px-4 py-3 font-body text-body leading-[1.75] whitespace-nowrap">
                    <span :class="code?.is_active ? 'text-green-600' : 'text-red-600'">
                      {{ code?.is_active ? 'Active' : 'Inactive' }}
                    </span>
                  </td>
                  <td class="px-4 py-3 whitespace-nowrap">
                    <button v-if="code?.id" @click="deleteDiscountCode(code.id)"
                      class="text-red-600 hover:text-red-700 font-body">Delete</button>
                  </td>
                </tr>
                <tr
                  v-if="code?.showEmails && code?.used_by_emails && Array.isArray(code.used_by_emails) && code.used_by_emails.length > 0"
                  class="bg-creme-50">
                  <td colspan="8" class="px-4 py-3">
                    <div class="font-body text-body leading-[1.75] text-sm">
                      <p class="font-header text-[1.5rem] font-bold mb-2">Used by:</p>
                      <div class="flex flex-wrap gap-2">
                        <span v-for="email in code.used_by_emails" :key="email"
                          class="px-2 py-1 bg-white rounded border border-gray-300 font-body text-body leading-[1.75]">
                          {{ email }}
                        </span>
                      </div>
                    </div>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>
        <div v-if="discountPagination && discountPagination.total_pages > 1"
          class="p-4 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 border-t border-creme-200">
          <button @click="loadDiscountCodes(discountCurrentPage - 1)" :disabled="!discountPagination.prev_page"
            :class="['px-4 py-2 rounded-md font-body text-body', discountPagination.prev_page ? 'bg-green-700 text-white hover:bg-green-800' : 'bg-gray-300 text-gray-500 cursor-not-allowed']">
            Previous
          </button>
          <span class="font-body text-body text-gray-700">
            Page {{ discountPagination.page }} of {{ discountPagination.total_pages }} ({{ discountPagination.total }}
            total)
          </span>
          <button @click="loadDiscountCodes(discountCurrentPage + 1)" :disabled="!discountPagination.next_page"
            :class="['px-4 py-2 rounded-md font-body text-body', discountPagination.next_page ? 'bg-green-700 text-white hover:bg-green-800' : 'bg-gray-300 text-gray-500 cursor-not-allowed']">
            Next
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { adminService } from '../../services/admin'
import Loader from '../Loader.vue'
import BaseModal from './BaseModal.vue'
import { useLoadingState } from '../../composables/useLoadingState'
import { formatDate, formatDateTime, formatDateForFilename } from '../../utils/date'
import { generateReceiptHTML } from '../../utils/receipt'
import html2pdf from 'html2pdf.js'
import { useScrollToTop } from '../../composables/useScrollToTop'

const { scrollToTop } = useScrollToTop()

const activeTab = ref('sales')
const sales = ref([])

// Receipt modal state
const showReceiptModal = ref(false)
const receiptModalContent = ref('')
const currentReceiptGroup = ref(null)

const discountCodes = ref([])
const showDiscountForm = ref(false)
const editingDiscountCode = ref(null)
const discountCurrentPage = ref(1)
const discountLimit = ref(10)
const discountPagination = ref(null)
const discountLoading = ref(false)
const discountError = ref(false)

const safeDiscountCodes = computed(() => {
  if (!Array.isArray(discountCodes.value)) {
    return []
  }
  return discountCodes.value.filter(code => code && typeof code === 'object')
})

const discountFormData = ref({
  code: '',
  description: null,
  discount_type: 'percentage',
  discount_value: 0,
  min_purchase_amount: null,
  max_discount_amount: null,
  usage_limit: null,
  valid_from: '',
  valid_until: null
})

const { loading, setLoading } = useLoadingState(false)
const currentPage = ref(1)
const pagination = ref(null)
const itemsPerPage = 10

// Donations tab state
const donations = ref([])
const donationsLoading = ref(false)

const goToPage = (page) => {
  const totalPages = pagination.value?.total_pages ?? 1
  if (typeof page !== 'number' || page < 1 || page > totalPages || page === currentPage.value) {
    return
  }
  loadSales(page)
}

// Page numbers with ellipses, e.g. [1, '…', 4, 5, 6, '…', 12]. String entries are
// non-clickable ellipses; the two sentinels keep :key unique when both appear.
const visiblePages = computed(() => {
  const totalPages = pagination.value?.total_pages ?? 1
  if (totalPages <= 1) return []
  const current = currentPage.value
  const pages = [1]
  const start = Math.max(2, current - 1)
  const end = Math.min(totalPages - 1, current + 1)
  if (start > 2) pages.push('ellipsis-1')
  for (let p = start; p <= end; p++) pages.push(p)
  if (end < totalPages - 1) pages.push('ellipsis-2')
  if (totalPages > 1) pages.push(totalPages)
  return pages
})

const groupedSales = computed(() => {
  const grouped = {}
  sales.value.forEach(sale => {
    if (!sale || !sale.id) return

    const transactionId = sale.order_number || sale.id.toString()
    if (!grouped[transactionId]) {
      grouped[transactionId] = {
        id: sale.id.toString(),
        user_id: sale.user_id ? sale.user_id.toString() : 'N/A',
        transaction_id: transactionId,
        customer_name: sale.customer_name || 'Guest Customer',
        shipping_address: sale.shipping_address || null,
        billing_address: sale.billing_address || null,
        delivery_option: sale.delivery_option || null,
        purchase_date: sale.created_at || new Date().toISOString(),
        discount_code: sale.discount_code || null,
        sale: {
          subtotal: sale.subtotal,
          discount_amount: sale.discount_amount,
          total_amount: sale.total_amount
        },
        items: []
      }
    }

    if (sale.items && Array.isArray(sale.items) && sale.items.length > 0) {
      const ticketGroups = {}

      sale.items.forEach(item => {
        if (!item || !item.name) return

        if (item.event_id) {
          const key = `${item.event_id}-${item.name}`
          if (!ticketGroups[key]) {
            ticketGroups[key] = {
              book_id: null,
              event_id: item.event_id,
              book_title: item.name,
              book_author: item.ticket_holder_name ? `${item.ticket_holder_name} (Holder)` : '',
              format: 'Ticket',
              price: (item.price || 0).toString(),
              original_price: (item.price || 0).toString(),
              quantity: 0
            }
          }
          ticketGroups[key].quantity += (item.quantity || 1)
        } else {
          grouped[transactionId].items.push({
            book_id: item.book_id || null,
            book_title: item.book_title || item.name,
            book_author: item.author || '',
            format: item.format || 'N/A',
            price: (item.price || 0).toString(),
            original_price: item.original_price ? item.original_price.toString() : (item.price || 0).toString(),
            discount_percentage: item.discount_percentage ? item.discount_percentage.toString() : null,
            quantity: item.quantity || 1
          })
        }
      })

      Object.values(ticketGroups).forEach(ticket => {
        grouped[transactionId].items.push(ticket)
      })
    }
  })

  return Object.values(grouped).map(order => {
    const saleSubtotal = order.sale?.subtotal ? parseFloat(order.sale.subtotal) : null
    const saleDiscountAmount = order.sale?.discount_amount ? parseFloat(order.sale.discount_amount) : null
    const saleTotal = order.sale?.total_amount ? parseFloat(order.sale.total_amount) : null

    let subtotal = saleSubtotal
    let totalDiscount = saleDiscountAmount
    let total = saleTotal

    if (subtotal === null || totalDiscount === null || total === null) {
      subtotal = parseFloat(order.items.reduce((sum, item) => {
        const itemPrice = parseFloat(item.original_price) || 0
        const itemQty = item.quantity || 1
        return sum + itemPrice * itemQty
      }, 0))

      totalDiscount = parseFloat(order.items.reduce((sum, item) => {
        return sum + parseFloat(item.discount_amount || 0)
      }, 0))

      total = Math.max(0, subtotal - totalDiscount)
    }

    return {
      ...order,
      subtotal,
      total_discount: totalDiscount || 0,
      total: total || Math.max(0, subtotal - (totalDiscount || 0))
    }
  }).sort((a, b) => {
    const dateA = new Date(a.purchase_date)
    const dateB = new Date(b.purchase_date)
    return dateB - dateA
  })
})

const loadSales = async (page = 1) => {
  setLoading(true)
  try {
    const response = await adminService.getSales(page, itemsPerPage)
    if (response && response.data && Array.isArray(response.data)) {
      sales.value = response.data
      pagination.value = response.pagination || null
      currentPage.value = page
    } else {
      sales.value = []
      pagination.value = null
    }
  } catch (error) {
    console.error('Failed to load sales:', error)
    sales.value = []
    pagination.value = null
  } finally {
    setLoading(false)
  }
}

const loadDonations = async () => {
  donationsLoading.value = true
  try {
    const data = await adminService.getDonations(1, 100)
    donations.value = Array.isArray(data) ? data : []
  } catch (error) {
    console.error('Failed to load donations:', error)
    donations.value = []
  } finally {
    donationsLoading.value = false
  }
}

function viewReceipt(group) {
  currentReceiptGroup.value = group
  // Admin doesn't have tickets data, so pass null for tickets
  const receiptHTML = generateReceiptHTML(group, true, null, null)
  receiptModalContent.value = receiptHTML
  showReceiptModal.value = true
}

function closeReceiptModal() {
  showReceiptModal.value = false
  document.body.style.overflow = ''
  receiptModalContent.value = ''
  currentReceiptGroup.value = null
}

// Watch for modal close to ensure body scroll is always restored
watch(showReceiptModal, (isOpen) => {
  if (isOpen) {
    document.body.style.overflow = 'hidden'
  } else {
    document.body.style.overflow = ''
  }
})

async function downloadReceipt() {
  if (!currentReceiptGroup.value) return

  const group = currentReceiptGroup.value
  // Generate full HTML document for PDF
  const receiptHTML = generateReceiptHTML(group, false, null, null)

  const tempWindow = window.open('', '_blank')
  if (!tempWindow) {
    alert('Please allow popups to download the PDF')
    return
  }

  tempWindow.document.write(receiptHTML)
  tempWindow.document.close()

  // Wait for proper HTML rendering and image loading
  await new Promise(resolve => setTimeout(resolve, 1000))

  const element = tempWindow.document.body

  const opt = {
    margin: [0.5, 0.5, 0.5, 0.5],
    filename: `receipt-${group.transaction_id}-${formatDateForFilename(group.purchase_date)}.pdf`,
    image: { type: 'jpeg', quality: 0.98 },
    html2canvas: {
      scale: 2,
      useCORS: true,
      allowTaint: true,
      windowWidth: Math.max(element.scrollWidth, 800),
      windowHeight: Math.max(element.scrollHeight, 1000),
      onclone: function (clonedDoc) {
        // Ensure proper styling is applied to cloned document
        const tables = clonedDoc.querySelectorAll('table');
        tables.forEach(table => {
          table.style.pageBreakInside = 'auto';
          table.style.tableLayout = 'auto';
        });
      }
    },
    jsPDF: {
      unit: 'in',
      format: 'letter',
      orientation: 'portrait',
      compress: true
    },
    pagebreak: {
      mode: ['avoid-all', 'css', 'legacy'],
      before: '.page-break-before',
      after: '.page-break-after',
      avoid: ['thead', 'tfoot', '.no-break']
    }
  }

  try {
    await html2pdf().set(opt).from(element).save()
    tempWindow.close()
  } catch (error) {
    console.error('Error generating PDF:', error)
    tempWindow.close()
    alert('Failed to generate PDF. Please try again.')
  }
}

const loadDiscountCodes = async (page = 1) => {
  discountLoading.value = true
  discountError.value = false
  try {
    const response = await adminService.getDiscountCodes(page, discountLimit.value)
    if (response && response.data && response.pagination) {
      const codesData = Array.isArray(response.data) ? response.data : []
      discountCodes.value = codesData.map((code, index) => {
        if (!code || typeof code !== 'object') {
          console.warn('Invalid code object:', code)
          return null
        }
        if (!code.id && code.id !== 0) {
          console.warn('Code missing id:', code)
          code.id = `temp-${index}-${Date.now()}`
        }
        return {
          ...code,
          showEmails: false
        }
      }).filter(Boolean)
      discountPagination.value = response.pagination
      discountCurrentPage.value = page
    } else {
      console.warn('Invalid response structure:', response)
      discountCodes.value = []
      discountPagination.value = null
    }
  } catch (error) {
    console.error('Failed to load discount codes:', error)
    discountError.value = true
    discountCodes.value = []
    discountPagination.value = null
  } finally {
    discountLoading.value = false
  }
}

const cancelDiscountForm = () => {
  showDiscountForm.value = false
  editingDiscountCode.value = null
  discountFormData.value = {
    code: '',
    description: null,
    discount_type: 'percentage',
    discount_value: 0,
    min_purchase_amount: null,
    max_discount_amount: null,
    usage_limit: null,
    valid_from: '',
    valid_until: null
  }
  scrollToTop()
}

const saveDiscountCode = async () => {
  try {
    const payload = {
      code: discountFormData.value.code,
      description: discountFormData.value.description || null,
      discount_type: discountFormData.value.discount_type,
      discount_value: discountFormData.value.discount_value,
      min_purchase_amount: discountFormData.value.min_purchase_amount || null,
      max_discount_amount: discountFormData.value.max_discount_amount || null,
      usage_limit: discountFormData.value.usage_limit || null,
      valid_from: discountFormData.value.valid_from ? new Date(discountFormData.value.valid_from).toISOString() : null,
      valid_until: discountFormData.value.valid_until ? new Date(discountFormData.value.valid_until).toISOString() : null
    }

    await adminService.createDiscountCode(payload)
    await loadDiscountCodes(discountCurrentPage.value)
    cancelDiscountForm()
  } catch (error) {
    console.error('Failed to save discount code:', error)
    alert('Failed to save discount code. Please try again.')
  }
}

const deleteDiscountCode = async (id) => {
  if (!confirm('Are you sure you want to delete this discount code?')) return

  try {
    await adminService.deleteDiscountCode(id)
    await loadDiscountCodes(discountCurrentPage.value)
  } catch (error) {
    console.error('Failed to delete discount code:', error)
    alert('Failed to delete discount code. Please try again.')
  }
}

const formatDiscountDate = formatDateTime

watch(activeTab, (newTab) => {
  if (newTab === 'discounts' && discountCodes.value.length === 0) {
    loadDiscountCodes(1)
  } else if (newTab === 'donations' && donations.value.length === 0) {
    loadDonations()
  }
})

onMounted(() => {
  loadSales()
})
</script>

<style scoped>
.hidden-print {
  display: none;
}

.print-only {
  display: block !important;
}
</style>

<style>
@media print {
  @page {
    margin: 0;
    size: A4;
  }

  html,
  body {
    margin: 0;
    padding: 0;
    height: 100%;
    width: 100%;
  }

  body * {
    visibility: hidden;
  }

  #print-invoice,
  #print-invoice * {
    visibility: visible;
  }

  #print-invoice {
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    display: block !important;
  }

  .invoice-container {
    max-width: 800px;
    margin: 0 auto;
    padding: 40px;
    background: white;
    font-family: 'Georgia', serif;
    color: #000;
    page-break-after: avoid;
  }

  .invoice-header {
    display: flex;
    justify-content: space-between;
    margin-bottom: 40px;
    padding-bottom: 20px;
    border-bottom: 2px solid #2D4D31;
  }

  .company-name {
    font-size: 28px;
    font-weight: bold;
    color: #2D4D31;
    margin: 0 0 8px 0;
  }

  .company-details {
    font-size: 18px;
    color: #666;
    margin: 0;
  }

  .invoice-meta {
    text-align: right;
    font-size: 14px;
  }

  .invoice-meta p {
    margin: 4px 0;
  }

  .invoice-billto {
    margin-bottom: 30px;
    padding: 15px;
    background: #FFF5EA;
    border-left: 4px solid #2D4D31;
  }

  .invoice-billto h2 {
    font-size: 16px;
    margin: 0 0 8px 0;
    color: #2D4D31;
  }

  .invoice-billto p {
    margin: 0;
    font-size: 14px;
  }

  .invoice-items {
    margin-bottom: 30px;
  }

  .invoice-items table {
    width: 100%;
    border-collapse: collapse;
  }

  .invoice-items thead {
    background: #2D4D31;
    color: white;
  }

  .invoice-items th {
    padding: 12px;
    text-align: left;
    font-weight: bold;
    font-size: 14px;
  }

  .invoice-items th.text-right {
    text-align: right;
  }

  .invoice-items tbody td {
    padding: 15px 12px;
    border-bottom: 1px solid #E8E8E8;
    font-size: 14px;
  }

  .invoice-items tfoot td {
    padding: 15px 12px;
    background: #FFF5EA;
    font-size: 16px;
  }

  .invoice-items tfoot td.text-right {
    text-align: right;
  }

  .item-title {
    font-weight: bold;
    margin-bottom: 4px;
  }

  .item-author {
    font-size: 12px;
    color: #666;
  }

  .invoice-footer {
    margin-top: 40px;
    padding-top: 20px;
    border-top: 2px solid #2D4D31;
    text-align: center;
  }

  .invoice-footer p {
    margin: 8px 0;
    font-size: 14px;
  }


  .footer-note {
    color: #666;
    font-size: 12px;
  }
}

@media screen {
  .hidden-print {
    display: none;
  }
}
</style>
